import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BedDouble, Bath, PlayCircle, Ruler, MapPin, Building2, Store } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { CinematicVideoDialogContent } from "@/components/media/CinematicVideoDialogContent";
import { localizePath } from "@/i18n/translations";
import { resolveImageUrl } from "@/lib/api";
import { deriveVideoDurationLabel, deriveVideoQualityBadge } from "@/lib/videoMetadataUtils";

const hasPositiveCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

export const PropertyCard = ({ lang, labels, property }) => {
  const listingLabel = labels[property.listing_type] || property.listing_type;
  const statusLabel = labels[property.availability] || property.availability;
  const hasVideo = Boolean(property.video_url);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);

  const posterSource = property.video_thumbnail_url || property.images?.[0] || "";
  const cardImageSource = resolveImageUrl(property.images?.[0]);
  const videoDurationLabel = useMemo(() => deriveVideoDurationLabel(property), [property]);
  const videoQualityBadge = useMemo(() => deriveVideoQualityBadge(property), [property]);

  return (
    <Card
      className="group overflow-hidden border-gold-500/20 bg-white shadow-lg transition-transform duration-300 hover:-translate-y-1"
      data-testid={`property-card-${property.id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden" data-testid={`property-card-image-wrapper-${property.id}`}>
        {cardImageSource ? (
          <img
            src={cardImageSource}
            alt={property.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            data-testid={`property-card-image-${property.id}`}
          />
        ) : (
          <div
            className="h-full w-full bg-gradient-to-br from-stone-200 to-stone-100"
            data-testid={`property-card-image-fallback-${property.id}`}
          />
        )}
        <div className="absolute left-4 right-4 top-4 flex justify-between" data-testid={`property-card-badges-${property.id}`}>
          <Badge className="bg-gold-600 text-cream-50" data-testid={`property-card-listing-badge-${property.id}`}>
            {listingLabel}
          </Badge>
          <Badge variant="outline" className="border-white/70 bg-white/80" data-testid={`property-card-status-badge-${property.id}`}>
            {statusLabel}
          </Badge>
        </div>

        {hasVideo && (
          <>
            <div
              className="absolute bottom-4 left-4 flex items-center gap-1.5"
              data-testid={`property-card-video-overlay-${property.id}`}
            >
              <span
                className="inline-flex rounded-full border border-white/35 bg-black/55 px-2.5 py-1 text-[11px] font-medium text-cream-50 backdrop-blur"
                data-testid={`property-card-video-duration-chip-${property.id}`}
              >
                {videoDurationLabel}
              </span>
              {videoQualityBadge && (
                <span
                  className="inline-flex rounded-full border border-[#D4AF37]/50 bg-black/55 px-2.5 py-1 text-[11px] font-medium text-[#E2C363] backdrop-blur"
                  data-testid={`property-card-video-quality-chip-${property.id}`}
                >
                  {videoQualityBadge}
                </span>
              )}
            </div>

            <span
              className="absolute bottom-4 right-4 inline-flex items-center gap-1 rounded-full bg-black/70 px-3 py-1 text-xs text-white"
              data-testid={`property-card-video-chip-${property.id}`}
            >
              <PlayCircle size={13} /> {labels.watchVideo}
            </span>
          </>
        )}
      </div>

      <CardContent className="space-y-4 p-5" data-testid={`property-card-content-${property.id}`}>
        <h3 className="text-lg font-semibold text-stone-900" data-testid={`property-card-title-${property.id}`}>
          {property.title}
        </h3>

        <p className="flex items-center gap-2 text-sm text-stone-600" data-testid={`property-card-location-${property.id}`}>
          <MapPin size={16} />
          {property.location}
        </p>

        <p className="text-xl font-semibold text-gold-700" data-testid={`property-card-price-${property.id}`}>
          {property.price.toLocaleString()} {property.currency}
        </p>

        <div className="flex flex-wrap gap-3 text-xs text-stone-600" data-testid={`property-card-stats-${property.id}`}>
          {hasPositiveCount(property.bedrooms) && (
            <span className="flex items-center gap-1" data-testid={`property-card-bedroom-${property.id}`}>
              <BedDouble size={14} /> {property.bedrooms}
            </span>
          )}
          {hasPositiveCount(property.bathrooms) && (
            <span className="flex items-center gap-1" data-testid={`property-card-bathroom-${property.id}`}>
              <Bath size={14} /> {property.bathrooms}
            </span>
          )}
          {hasPositiveCount(property.floors_count) && (
            <span className="flex items-center gap-1" data-testid={`property-card-floors-${property.id}`}>
              <Building2 size={14} /> {property.floors_count}
            </span>
          )}
          {hasPositiveCount(property.units_count) && (
            <span className="flex items-center gap-1" data-testid={`property-card-units-${property.id}`}>
              <Store size={14} /> {property.units_count}
            </span>
          )}
          <span className="flex items-center gap-1" data-testid={`property-card-area-${property.id}`}>
            <Ruler size={14} /> {property.area_sqm}
          </span>
        </div>

        <div className="grid gap-2" data-testid={`property-card-action-grid-${property.id}`}>
          {hasVideo && (
            <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full border-gold-500/40 bg-black/[0.03] text-stone-800 hover:bg-gold-50"
                onClick={() => setVideoDialogOpen(true)}
                data-testid={`property-card-watch-video-button-${property.id}`}
              >
                <PlayCircle size={15} />
                {labels.watchVideo}
              </Button>

              <CinematicVideoDialogContent
                open={videoDialogOpen}
                videoUrl={property.video_url}
                posterUrl={posterSource}
                title={labels.videoDialogTitle}
                description={labels.videoDialogDescription}
                noVideoLabel={labels.noVideo}
                clickToPlayLabel={labels.watchVideo}
                testIdPrefix={`property-card-video-dialog-${property.id}`}
              />
            </Dialog>
          )}

          <Link to={localizePath(lang, `/properties/${property.id}`)} data-testid={`property-card-link-${property.id}`}>
            <Button className="w-full rounded-full bg-stone-900 text-cream-50 hover:bg-stone-700" data-testid={`property-card-details-button-${property.id}`}>
              {labels.details}
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};
